/*
 * The server-rendered preview (Q21, spec 12 §4.4): the draft goes to the server 400 ms after the
 * last keystroke, only while the pane is open, and comes back as the node tree the timeline
 * renders — through the timeline's own renderer, so the two cannot drift.
 */

import { useEffect, useState, type ComponentType } from "react"
import { t } from "../i18n/index.ts"
import type { Render } from "./types.ts"

/**
 * The pause after the last keystroke before the draft is rendered. The render endpoint's bucket is
 * 30 a minute (A-05b §7.1) and a typed sentence used up half of it at 150 ms; at 400 ms a preview
 * still feels immediate and one sentence costs one request.
 */
export const PREVIEW_DEBOUNCE_MS = 400

interface PreviewPaneProps {
	markdown: string
	preview: (markdown: string) => Promise<Render>
	Render: ComponentType<{ render: Render }>
}

type Outcome = { render: Render } | { failed: true }

export function PreviewPane({ markdown, preview, Render }: PreviewPaneProps) {
	const [outcome, setOutcome] = useState<Outcome | null>(null)
	const empty = markdown.trim() === ""

	// The last answer stays on screen until the next one arrives; a preview that blinks on every
	// keystroke is worse than one that lags a moment.
	useEffect(() => {
		if (empty) return
		let current = true
		const timer = setTimeout(() => {
			preview(markdown)
				.then((render) => {
					if (current) setOutcome({ render })
				})
				.catch(() => {
					if (current) setOutcome({ failed: true })
				})
		}, PREVIEW_DEBOUNCE_MS)
		return () => {
			current = false
			clearTimeout(timer)
		}
	}, [empty, markdown, preview])

	const shown = empty ? null : outcome
	return (
		<section className="tb-composer__preview" aria-label={t("composer.preview.title")}>
			{shown === null ? (
				<p className="tb-composer__status">{t("composer.preview.empty")}</p>
			) : "failed" in shown ? (
				<p className="tb-composer__status" role="status">
					{t("composer.preview.failed")}
				</p>
			) : (
				<Render render={shown.render} />
			)}
		</section>
	)
}
